import { useEffect, useState } from "react";
import { Switch, Route, Router, Link, useLocation } from "wouter";
import { useHashLocation } from "wouter/use-hash-location";
import { ThemeProvider, useTheme } from "next-themes";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { AuthProvider } from "@/lib/auth";
import NotFound from "@/pages/not-found";
import ArenaPage from "@/pages/arena";
import BotsPage from "@/pages/bots";
import DuelPage from "@/pages/duel";
import HumanDuelPage from "@/pages/human-duel";
import LeaderboardPage from "@/pages/leaderboard";
import CasesPage from "@/pages/cases";
import TournamentPage from "@/pages/tournament";
import ExternalBotsPage from "@/pages/external-bots";
import ExternalMatchesPage from "@/pages/external-matches";
import ApiDocsPage from "@/pages/api-docs";
import SdkExamplesPage from "@/pages/sdk-examples";
import PricingPage from "@/pages/pricing";
import { PerplexityAttribution } from "@/components/PerplexityAttribution";
import { Swords, Bot, Trophy, Flame, BookOpen, Medal, Globe, Zap, Code, CreditCard, FileText, Sun, Moon } from "lucide-react";

function ThemeToggle() {
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  if (!mounted) {
    return <div className="h-9 w-9 shrink-0" aria-hidden />;
  }

  const isDark = resolvedTheme === "dark";
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-9 w-9 shrink-0 text-muted-foreground"
      onClick={() => setTheme(isDark ? "light" : "dark")}
      title={isDark ? "Светлая тема" : "Тёмная тема"}
      data-testid="button-theme-toggle"
    >
      {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </Button>
  );
}

function NavLink({ href, children, icon: Icon }: { href: string; children: React.ReactNode; icon: any }) {
  const [location] = useLocation();
  const isActive = location === href || (href === "/" && location === "");
  return (
    <Link href={href}>
      <span
        className={`flex items-center gap-1.5 px-2.5 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
          isActive
            ? "bg-primary/10 text-primary"
            : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
        }`}
        data-testid={`nav-${href.replace("/", "") || "home"}`}
      >
        <Icon className="w-4 h-4" />
        {children}
      </span>
    </Link>
  );
}

function Layout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-border/50 bg-card/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link href="/">
            <span className="flex items-center gap-2 cursor-pointer shrink-0" data-testid="logo">
              <Flame className="w-5 h-5 text-primary" />
              <span className="font-semibold text-sm tracking-tight">Арена поединков</span>
            </span>
          </Link>
          <nav className="flex items-center gap-0.5 overflow-x-auto ml-4 scrollbar-none flex-1 min-w-0">
            <NavLink href="/" icon={Swords}>Арена</NavLink>
            <NavLink href="/cases" icon={BookOpen}>Кейсы</NavLink>
            <NavLink href="/tournament" icon={Medal}>Чемпионат</NavLink>
            <NavLink href="/bots" icon={Bot}>Боты</NavLink>
            <NavLink href="/leaderboard" icon={Trophy}>Рейтинг</NavLink>
            <span className="w-px h-6 bg-border/50 mx-1 shrink-0" />
            <NavLink href="/external-bots" icon={Globe}>Внешние боты</NavLink>
            <NavLink href="/external-matches" icon={Zap}>Матчи</NavLink>
            <NavLink href="/api-docs" icon={FileText}>API</NavLink>
            <NavLink href="/sdk" icon={Code}>SDK</NavLink>
            <NavLink href="/pricing" icon={CreditCard}>Тарифы</NavLink>
          </nav>
          <ThemeToggle />
        </div>
      </header>
      <main className="flex-1">
        {children}
      </main>
      <footer className="border-t border-border/50 py-4">
        <div className="max-w-7xl mx-auto px-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <PerplexityAttribution />
          <p className="text-xs text-muted-foreground tabular-nums" data-testid="text-app-version">
            v{import.meta.env.VITE_APP_VERSION}
          </p>
        </div>
      </footer>
    </div>
  );
}

function AppRouter() {
  return (
    <Layout>
      <Switch>
        <Route path="/" component={ArenaPage} />
        <Route path="/cases" component={CasesPage} />
        <Route path="/tournament" component={TournamentPage} />
        <Route path="/bots" component={BotsPage} />
        <Route path="/duel/:id" component={DuelPage} />
        <Route path="/human-duel/:id" component={HumanDuelPage} />
        <Route path="/leaderboard" component={LeaderboardPage} />
        <Route path="/external-bots" component={ExternalBotsPage} />
        <Route path="/external-matches" component={ExternalMatchesPage} />
        <Route path="/api-docs" component={ApiDocsPage} />
        <Route path="/sdk" component={SdkExamplesPage} />
        <Route path="/pricing" component={PricingPage} />
        <Route component={NotFound} />
      </Switch>
    </Layout>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false} storageKey="negotiation-arena-theme">
        <TooltipProvider>
          <AuthProvider>
            <Toaster />
            <Router hook={useHashLocation}>
              <AppRouter />
            </Router>
          </AuthProvider>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
